import { Renderer3D } from "./renderer-3D.service";
import { Injector, ComponentFactoryResolver, ComponentRef, Component } from "@angular/core";
import { GisLayerService } from "../../../../gis/services/gis-layer.service";
import { Observable, from, merge } from "rxjs";
import { concatMap, tap  } from "rxjs/operators";
import { GisViewService } from "../../../../gis/services/gis-view.service";
import { Renderer2D } from "./Renderer-2D.service";
import { SceneOverlayComponent } from "./overlays/scene-overlay/scene-overlay.component";
import { GisQueryService } from "../../../services/gis-query.service";
import { GISConfig } from "../../../../gis/models/gis-config.model";
import { CONFIG } from "./renderer.interface";
import { CasingPlanModel, WellSegmentModel } from "../../../../form-controls/components/new-casing-plan/models/casing-plan.model";
import { CasingPlanInformationComponent } from "./casing-plan-components/casing-plan-information/casing-plan-information.component";
import { ForeignWellInformationComponent } from "./casing-plan-components/foreign-well-information/foreign-well-information.component";
import { IntersectionErrorComponent } from "./casing-plan-components/intersection-error/intersection-error.component";

export class CasingPlanSceneRenderer3D extends Renderer3D {
    public _gisLayerService: GisLayerService;
    public _gisViewService: GisViewService;
    public _gisQueryService: GisQueryService;
    public _componentFactory: ComponentFactoryResolver;
    config: GISConfig;

    constructor(
        public _casingPlan: CasingPlanModel,
        _injector: Injector) {
        super(_injector);
        this.config = window["GIS.Conf"] as GISConfig;
        this._gisLayerService = _injector.get(GisLayerService);
        this._gisViewService = _injector.get(GisViewService);
        this._gisQueryService = _injector.get(GisQueryService);
        this._componentFactory = _injector.get(ComponentFactoryResolver);
    }

    initialize() {
        return super.initialize().pipe(tap(() => {
            this.addHelperWidget();

            this._view.on("click",
                (event) => {
                    this._view.hitTest(event).then(response => {
                        if (response.results[0].graphic) {
                            let comp = this._createComponent(response);

                            event.stopPropagation();
                            if (comp) {
                                this._view.popup.open({
                                    location: event.mapPoint,
                                    title: comp.title,
                                    content: comp.component.location.nativeElement
                                });
                            }
                        }
                    });
                });
        }));
    }

    render(): Observable<any> {
		return from(this._gisLayerService.add3DLayers(this.scene)).pipe(
			concatMap(() => {
				let tempArray: WellSegmentModel[] = [];
				tempArray = tempArray.concat(this._casingPlan.wellSegments);

				this._gisLayerService.clearLineGraphics();
				return merge(
					from(this._gisViewService.getGroundZValue(this.rendererConfig.center.longitude, this.rendererConfig.center.latitude, this.scene).then((x) => {
						return this.setCameraPosition({
							y: this.rendererConfig.center.latitude - 0.0025,
							z: (x.geometry as any).z + 300,
						},
							0,
							45
						).then(async () => {
							this.defaultCameraPosition = {
								point: { x: this.camera.position.x, y: this.camera.position.y, z: this.camera.position.z },
								heading: this.camera.heading,
								tilt: this.camera.tilt
							}

							await this._mapNearbyWells();
						});
					})),
					from(this._gisLayerService.addLineGraphics(
						tempArray,
						this._view
					))
				);
			})
		);

    }

    addHelperWidget(): void {
        let componentFactory: ComponentFactoryResolver = this.injector.get(ComponentFactoryResolver);
        let factory = componentFactory.resolveComponentFactory(SceneOverlayComponent);
        let comp = factory.create(this.injector);
        comp.instance.renderer = this;
        this.view.ui.add(comp.location.nativeElement, 'top-right');
        comp.changeDetectorRef.detectChanges();
    }

    public _createComponent(response): { component: ComponentRef<any>, title: string } {
        let compFac: ComponentRef<any>;
        let title = "";
        switch (response.results[0].graphic.layer.id) {
            case 'polylineLyr':
                compFac = this._componentFactory.resolveComponentFactory(CasingPlanInformationComponent).create(this.injector);
                compFac.instance.casingPlan = response.results[0].graphic.attributes;
                title = response.results[0].graphic.getAttribute("casingType");
                break;
            case 'wellStringsLyr':
                //compFac = this._componentFactory.resolveComponentFactory(ForeignWellInformationComponent).create(this.injector);
                //compFac.instance.wellString = response.results[0].graphic.attributes;
                break;
            default:
                return null;
        }
        compFac.changeDetectorRef.detectChanges();

        return { component: compFac, title: title };
    }

    public _mapNearbyWells(): void {
        let points: number[][] = [];
        this._casingPlan.wellSegments.forEach(x => {
            x.gisObjects.forEach(y => {
                points.push([y.longitude, y.latitude, y.tvd == 0 ? 0 : y.tvd * -1]);
            });
        });

        let casingDiameter = this._casingPlan.wellSegments[0].boreholeDiameter / 2;
        this._gisQueryService.queryBufferPolyline(this.config.wellStringsUrl.substr(0, this.config.wellStringsUrl.length - 2), "Well String", points, casingDiameter + 1000).then((result) => {
            if (result.features.length > 0) {
                let comp = this._componentFactory.resolveComponentFactory(IntersectionErrorComponent).create(this.injector);
                this._view.popup.open({
                    location: this._view.center,
                    title: "Intersection Alert",
                    content: comp.location.nativeElement
                });
            }
        });
    }
}

export class CasingPlanSceneRenderer2D extends Renderer2D {
    public _gisLayerService: GisLayerService;
    public _gisViewService: GisViewService;

    constructor(
        injector: Injector) {
        super(injector);

        this._gisLayerService = this.injector.get(GisLayerService);
        this._gisViewService = this.injector.get(GisViewService);
    }

    render() {
        return from(this._gisLayerService.add2DLayers(this.map)).pipe(concatMap(() => {
            return (this._gisLayerService.add2DPoint(this.config.center.longitude, this.config.center.latitude, 'Well Head',
                this.map));
        }));
    }
}
